/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil1Icon } from '@radix-ui/react-icons';
import { Text, Title, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

interface SectionCardProps {
  title: string;
  children: React.ReactNode;
  className?: string;
  titleAccessory?: React.ReactNode;
  'data-testid'?: string;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  title,
  children,
  className,
  titleAccessory,
  'data-testid': dataTestId,
}) => (
  <div
    data-testid={dataTestId}
    className={cn(
      'p-[var(--space-4)] rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-card)]',
      className,
    )}
  >
    <div className="flex items-center justify-between pb-[var(--space-3)] mb-[var(--space-3)] border-b border-[var(--border-subtle)]">
      {/* `Title` resolves fontSize/fontWeight from `order` into an inline
          style, which no class can override -- order 4 would render this
          card heading at --text-h3, a clamp that reaches 24px and only at
          semibold. These headings are --text-app-section at --font-bold,
          which no class could state. Stating it in `style` is what wins,
          because the caller's style is spread last. */}
      <Title
        order={4}
        style={{ fontSize: 'var(--text-app-section)', fontWeight: 'var(--font-bold)' }}
        // No all-caps, and no extra letter-spacing with it: the wider
        // tracking exists to keep capitals legible, so without them it
        // only reads as loose.
        className="m-0 text-[var(--seed-base)]"
      >
        {title}
      </Title>
      {titleAccessory}
    </div>
    {children}
  </div>
);

interface InfoRowProps {
  label: string;
  value: string;
}

export const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
  // Top-aligned, not centred: an address long enough to wrap used to leave
  // the label floating against the middle of a two-line value. The label
  // holds its width and the value takes the rest, wrapping to the right.
  <div className="flex items-start justify-between gap-[var(--space-3)] py-[var(--space-2)]">
    <Text
      as="span"
      className="shrink-0 text-[length:var(--text-body-sm)] leading-[var(--leading-snug)] text-[var(--text-tertiary)]"
    >
      {label}
    </Text>
    <Text
      as="span"
      className="min-w-0 break-words text-right text-[length:var(--text-body-sm)] [font-weight:var(--font-bold)] leading-[var(--leading-snug)] text-[var(--text-primary)]"
    >
      {value}
    </Text>
  </div>
);

/**
 * The pencil in a card's header that opens the property editor on the
 * section that card shows.
 */
export const EditCardButton: React.FC<{ onClick: () => void; 'data-testid'?: string }> = ({
  onClick,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation('features/properties');
  return (
    <UnstyledButton
      type="button"
      onClick={onClick}
      data-testid={dataTestId}
      aria-label={t('detailView.editCard')}
      title={t('detailView.editCard')}
      className="flex size-[28px] shrink-0 items-center justify-center rounded-(--radius-sm) text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]"
    >
      <Pencil1Icon width={14} height={14} aria-hidden />
    </UnstyledButton>
  );
};

/** What an always-visible card says while nothing has been filled in. */
export const EmptyCardHint: React.FC = () => {
  const { t } = useTranslation('features/properties');
  return (
    <Text className="m-0 text-[length:var(--text-body-sm)] leading-[var(--leading-snug)] text-[var(--text-tertiary)]">
      {t('detailView.emptyCardHint')}
    </Text>
  );
};
